/**
 * Pure helpers for Capgo `onlyDownload` activation.
 *
 * Native autoUpdate never calls `next()` itself. JS has to pick a downloaded
 * bundle that is not the one currently running and queue it. Kept free of
 * Capacitor / axios so the choice can be unit-tested.
 */

const ACTIVATABLE = new Set(['success', 'pending'])

function isBuiltin(bundle) {
  const id = String(bundle?.id || '')
  const version = String(bundle?.version || '')
  return !id || id === 'builtin' || version === 'builtin'
}

/** @returns {number[]} */
export function parseBundleVersion(version) {
  return String(version || '')
    .split('.')
    .map(part => {
      const n = Number.parseInt(part, 10)
      return Number.isFinite(n) ? n : 0
    })
}

/** @returns {number} negative if a < b, 0 if equal, positive if a > b */
export function compareBundleVersions(a, b) {
  const left = parseBundleVersion(a)
  const right = parseBundleVersion(b)
  const len = Math.max(left.length, right.length)
  for (let i = 0; i < len; i += 1) {
    const diff = (left[i] || 0) - (right[i] || 0)
    if (diff) return diff
  }
  return 0
}

function downloadedAt(bundle) {
  const raw = String(bundle?.downloaded || '')
  if (!raw || raw.startsWith('1970')) return 0
  const ms = Date.parse(raw)
  return Number.isFinite(ms) ? ms : 0
}

/**
 * Capgo's `list()` return shape has moved between plugin versions. Accept the
 * documented `{ bundles }` object, a bare array, and a single bundle.
 * @param {unknown} listed
 * @returns {object[]}
 */
export function bundlesFromListResult(listed) {
  if (!listed) return []
  if (Array.isArray(listed)) return listed.filter(Boolean)
  if (Array.isArray(listed.bundles)) return listed.bundles.filter(Boolean)
  if (Array.isArray(listed.bundle)) return listed.bundle.filter(Boolean)
  if (listed.id) return [listed]
  return []
}

/**
 * Newest downloaded bundle that is safe to pass to `next()`, or null.
 *
 * Never returns the currently running bundle, the embedded `builtin` bundle,
 * a failed/partial download, or a version older than what is already active.
 *
 * @param {object[]} bundles
 * @param {object|null|undefined} current
 */
export function pickActivatableBundle(bundles, current) {
  const currentId = String(current?.id || '')
  const currentVersion = isBuiltin(current)
    ? String(current?.version || '')
    : String(current?.version || currentId)

  const candidates = (Array.isArray(bundles) ? bundles : [])
    .filter(bundle => bundle && !isBuiltin(bundle))
    .filter(bundle => ACTIVATABLE.has(String(bundle.status || '')))
    .filter(bundle => String(bundle.id) !== currentId)
    .filter(bundle => {
      if (!currentVersion) return true
      return compareBundleVersions(bundle.version, currentVersion) > 0
    })

  if (!candidates.length) return null

  return candidates.reduce((best, bundle) => {
    const byVersion = compareBundleVersions(bundle.version, best.version)
    if (byVersion > 0) return bundle
    if (byVersion < 0) return best
    return downloadedAt(bundle) >= downloadedAt(best) ? bundle : best
  })
}
